import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Users } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { ALL_LANGUAGES } from "@/i18n/locales";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "D";
}

export function Members() {
  const { t } = useLanguage();
  const [members, setMembers] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clan/wall`);
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setMembers([]);
    }
  }, []);

  useEffect(() => {
    load();
    const onJoin = () => load();
    window.addEventListener("clan-joined", onJoin);
    const poll = setInterval(load, 20000);
    return () => {
      window.removeEventListener("clan-joined", onJoin);
      clearInterval(poll);
    };
  }, [load]);

  return (
    <section id="kindred" data-testid="members-section" className="relative bg-clan-bg py-28 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Reveal>
              <Overline>{t.members.overline}</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-clan-ink sm:text-5xl lg:text-6xl pearl">
                {t.members.title}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-clan-muted">{t.members.subtitle}</p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div
              data-testid="members-count"
              className="flex items-center gap-3 rounded-md border border-clan-accent/30 bg-clan-accent/5 px-5 py-3"
            >
              <Users className="h-4 w-4 text-clan-accent" />
              <span className="font-mono text-sm text-clan-ink">
                <span className="text-clan-accent">{members.length}</span> {t.members.countLabel}
              </span>
            </div>
          </Reveal>
        </div>

        <div className="mt-14">
          {members.length === 0 ? (
            <Reveal>
              <div className="rounded-lg border border-dashed border-clan-ink/15 p-14 text-center">
                <p className="font-serif text-2xl italic text-clan-muted">{t.members.empty}</p>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence>
                {members.map((m, i) => {
                  const flag = ALL_LANGUAGES.find((l) => l.code === m.language);
                  return (
                    <motion.div
                      key={m.id || i}
                      layout
                      data-testid={`member-card-${i}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: Math.min(i, 8) * 0.04 }}
                      className="group flex flex-col gap-4 rounded-lg border border-clan-ink/10 bg-clan-surface p-6 transition-colors duration-300 hover:border-clan-accent/40"
                    >
                      <div className="flex items-center gap-4">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md border border-clan-accent/40 font-serif text-base text-clan-accent">
                          {initials(m.name)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-serif text-lg text-clan-ink">{m.name || t.members.anon}</p>
                          {flag && (
                            <p className="font-mono text-[10px] uppercase tracking-widest text-clan-muted">{flag.native}</p>
                          )}
                        </div>
                      </div>
                      {m.message && (
                        <p className="border-l border-clan-accent/40 pl-4 text-sm italic leading-relaxed text-clan-muted">
                          “{m.message}”
                        </p>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
